import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Award } from 'lucide-react';
import api from '../../components/api';
import { toast } from 'react-toastify';
import useGlobale from '../../components/global';
import Sidebar from '../../components/SideBar';
import Header from '../../components/Header';
import DemandeurMenu from '../../components/DemandeurMenu';

import {
    DiplomeCard,
    DiplomeModal,
    DiplomeSearch,
    DiplomePagination,
    EmptyState,
    DeleteConfirmation,
    EMPTY_FORM_DATA,
    NIVEAUX_SANS_DOMAINE
} from './';

const DiplomePage = () => {
    const { user } = useGlobale();
    const navigate = useNavigate();

    // États
    const [loading, setLoading] = useState(true);
    const [diplomes, setDiplomes] = useState([]);
    const [niveauxEtude, setNiveauxEtude] = useState([]);
    const [typesDiplome, setTypesDiplome] = useState([]);
    const [domainesFormation, setDomainesFormation] = useState([]);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);

    // États pour le modal
    const [showModal, setShowModal] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [selectedDiplome, setSelectedDiplome] = useState(null);
    const [formData, setFormData] = useState(EMPTY_FORM_DATA);
    const [errors, setErrors] = useState({});
    const [showOtherType, setShowOtherType] = useState(false);

    // États pour la recherche et pagination
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(6);

    // États pour la suppression
    const [deleteConfirmation, setDeleteConfirmation] = useState(null);

    // Détermine si le domaine doit être affiché
    const showDomaine = () => {
        if (!formData.niveau_etude) return false;
        const niveau = niveauxEtude.find(n => String(n.id) === String(formData.niveau_etude))?.niveau || '';
        return !NIVEAUX_SANS_DOMAINE.some(n => niveau.toLowerCase().includes(n.toLowerCase()));
    };

    // Chargement initial
    useEffect(() => {
        fetchDiplomes();
        fetchNiveauxEtude();
        fetchTypesDiplome();
        fetchDomainesFormation();

        // Responsive sidebar
        const handleResize = () => setIsSidebarOpen(window.innerWidth >= 1024);
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // API Calls
    const fetchDomainesFormation = async () => {
        try {
            const response = await api.get('demandeur/domaines-formation/');
            setDomainesFormation(response.data);
        } catch (error) {
            console.error('Erreur lors de la récupération des domaines:', error);
        }
    };

    const fetchDiplomes = async () => {
        setLoading(true);
        try {
            const response = await api.get('demandeur/diplomes/');
            setDiplomes(response.data);
        } catch (error) {
            console.error('Erreur:', error);
            toast.error('Erreur lors de la récupération des diplômes');
        } finally {
            setLoading(false);
        }
    };

    const fetchNiveauxEtude = async () => {
        try {
            const response = await api.get('demandeur/niveaux-etude/');
            setNiveauxEtude(response.data);
        } catch (error) {
            console.error('Erreur:', error);
            toast.error('Erreur lors de la récupération des niveaux');
        }
    };

    const fetchTypesDiplome = async () => {
        try {
            const response = await api.get('demandeur/types-diplome/');
            setTypesDiplome(response.data);
        } catch (error) {
            console.error('Erreur:', error);
            toast.error('Erreur lors de la récupération des types');
        }
    };

    // Validation
    const validateForm = () => {
        const newErrors = {};
        if (!formData.diplome) newErrors.diplome = "Le type de diplôme est requis";
        if (formData.diplome === 'autre' && !formData.autre?.trim()) {
            newErrors.autre = "Veuillez préciser le type de diplôme";
        }
        if (!formData.date_obtention) newErrors.date_obtention = "La date d'obtention est requise";
        if (!formData.etablissement.trim()) newErrors.etablissement = "L'établissement est requis";
        if (!formData.niveau_etude) newErrors.niveau_etude = "Le niveau d'études est requis";
        if (showDomaine() && !formData.domaine_formation) {
            newErrors.domaine_formation = "Le domaine est requis pour ce niveau";
        }
        if (!formData.pays.trim()) newErrors.pays = "Le pays est requis";

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handlers
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => {
            const newData = { ...prev, [name]: value };
            if (name === 'diplome') {
                setShowOtherType(value === 'autre');
                if (value !== 'autre') newData.autre = '';
            }
            return newData;
        });
    };

    const handleCountryChange = (selectedOption) => {
        setFormData(prev => ({ ...prev, pays: selectedOption ? selectedOption.label : 'Niger' }));
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) setCurrentPage(newPage);
    };

    const handleItemsPerPageChange = (e) => {
        setItemsPerPage(Number(e.target.value));
        setCurrentPage(1);
    };

    // CRUD
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setLoading(true);
        try {
            const dataToSend = { ...formData, utilisateur: user.id };
            if (showOtherType) dataToSend.diplome = 'autre';

            if (isEditMode && selectedDiplome) {
                await api.put(`demandeur/diplomes/${selectedDiplome.id}/`, dataToSend);
                toast.success('Diplôme mis à jour avec succès');
            } else {
                await api.post('demandeur/diplomes/', dataToSend);
                toast.success('Diplôme ajouté avec succès');
            }

            fetchDiplomes();
            closeModal();
        } catch (error) {
            console.error('Erreur:', error);
            toast.error('Erreur lors de la sauvegarde');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteConfirmation) return;

        setLoading(true);
        try {
            await api.delete(`demandeur/diplomes/${deleteConfirmation}/`);
            toast.success('Diplôme supprimé avec succès');
            setDeleteConfirmation(null);
            fetchDiplomes();
        } catch (error) {
            console.error('Erreur:', error);
            toast.error('Erreur lors de la suppression');
        } finally {
            setLoading(false);
        }
    };

    // Modal
    const openAddModal = () => {
        setIsEditMode(false);
        setSelectedDiplome(null);
        setFormData(EMPTY_FORM_DATA);
        setShowOtherType(false);
        setErrors({});
        setShowModal(true);
    };

    const openEditModal = (diplome) => {
        setIsEditMode(true);
        setSelectedDiplome(diplome);
        setFormData({
            diplome: diplome.diplome,
            autre: diplome.autre || '',
            date_obtention: diplome.date_obtention,
            etablissement: diplome.etablissement,
            niveau_etude: diplome.niveau_etude,
            pays: diplome.pays,
            domaine_formation: diplome.domaine_formation || ''
        });
        setShowOtherType(diplome.diplome === 'autre' || diplome.diplome === null);
        setErrors({});
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setFormData(EMPTY_FORM_DATA);
        setErrors({});
        setShowOtherType(false);
    };

    // Pagination
    const getFilteredData = useCallback(() => {
        if (!searchTerm.trim()) return diplomes;
        return diplomes.filter(diplome => {
            const type = typesDiplome.find(t => t.id === diplome.diplome)?.nom || '';
            return (
                type.toLowerCase().includes(searchTerm.toLowerCase()) ||
                diplome.etablissement.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (diplome.autre && diplome.autre.toLowerCase().includes(searchTerm.toLowerCase()))
            );
        });
    }, [diplomes, searchTerm, typesDiplome]);

    const filteredData = getFilteredData();
    const totalPages = Math.ceil(filteredData.length / itemsPerPage);
    const paginatedData = filteredData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    return (
        <div className="flex min-h-screen bg-[#F4F8E9]">
            <Sidebar
                isOpen={isSidebarOpen}
                menuItems={DemandeurMenu()}
                onClose={() => setIsSidebarOpen(false)}
            />

            <div className="flex-1 flex flex-col w-full">
                <Header
                    isSidebarOpen={isSidebarOpen}
                    setIsSidebarOpen={setIsSidebarOpen}
                    user={user}
                />
                <main className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-8 pb-20 lg:pb-8 w-full">
                    {/* En-tête */}
                    <div className="flex flex-col md:flex-row md:items-center justify-between mb-8">
                        <div>
                            <h1 className="text-2xl font-bold text-[#437011] mb-2">
                                <Award className="inline-block mr-2" />
                                Diplômes
                            </h1>
                            <p className="text-gray-600">Gérez vos diplômes et certifications</p>
                        </div>
                        <button
                            onClick={openAddModal}
                            className="bg-orange-600 text-white px-4 py-2 rounded-lg flex items-center hover:bg-orange-700 transition mt-4 md:mt-0 w-full md:w-auto justify-center"
                        >
                            <Plus size={18} className="mr-2" />
                            Ajouter un diplôme
                        </button>
                    </div>

                    {/* Loading */}
                    {loading && diplomes.length === 0 ? (
                        <div className="flex justify-center items-center h-64">
                            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-orange-500"></div>
                        </div>
                    ) : (
                        <>
                            {/* Recherche */}
                            <DiplomeSearch
                                searchTerm={searchTerm}
                                onSearchChange={handleSearchChange}
                            />

                            {/* Liste des diplômes */}
                            {paginatedData.length === 0 ? (
                                <EmptyState onAdd={openAddModal} />
                            ) : (
                                <>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                        {paginatedData.map(diplome => (
                                            <DiplomeCard
                                                key={diplome.id}
                                                diplome={diplome}
                                                typesDiplome={typesDiplome}
                                                niveauxEtude={niveauxEtude}
                                                domainesFormation={domainesFormation}
                                                onEdit={openEditModal}
                                                onDelete={setDeleteConfirmation}
                                            />
                                        ))}
                                    </div>

                                    {/* Pagination */}
                                    <DiplomePagination
                                        currentPage={currentPage}
                                        totalPages={totalPages}
                                        itemsPerPage={itemsPerPage}
                                        onPageChange={handlePageChange}
                                        onItemsPerPageChange={handleItemsPerPageChange}
                                    />
                                </>
                            )}
                        </>
                    )}
                </main>
            </div>

            {/* Modal */}
            <DiplomeModal
                isOpen={showModal}
                isEditMode={isEditMode}
                formData={formData}
                errors={errors}
                typesDiplome={typesDiplome}
                niveauxEtude={niveauxEtude}
                domainesFormation={domainesFormation}
                showOtherType={showOtherType}
                onClose={closeModal}
                onSubmit={handleSubmit}
                onInputChange={handleInputChange}
                onCountryChange={handleCountryChange}
            />

            {/* Confirmation suppression */}
            {deleteConfirmation && (
                <DeleteConfirmation
                    onConfirm={handleDelete}
                    onCancel={() => setDeleteConfirmation(null)}
                />
            )}
        </div>
    );
};

export default DiplomePage;
